/**
 * Reusable Principal Selector Component
 *
 * Provides dropdown selection and badge display for Users, UserGroups, or Roles.
 * Uses server-side search with ServerSearchableDropdown.
 */

import { useCallback } from 'react';
import { Form, Row, Col, Badge, Button } from 'react-bootstrap';
import ServerSearchableDropdown from '@/components/common/ServerSearchableDropdown';
import { apiClient, PaginatedResponse, UserSummary, UserGroupSummary, RoleSummary } from '@/services/apiClient';

type PrincipalEntity = UserSummary | UserGroupSummary | RoleSummary;

interface PrincipalSelectorProps {
  type: 'users' | 'userGroups' | 'roles';
  label: string;
  selected: string[];
  onAdd: (value: string) => void;
  onRemove: (value: string) => void;
  color: 'primary' | 'info' | 'success';
  getDisplayValue: (value: string) => string;
  onRefresh?: () => void | Promise<void>;
  isRefreshing?: boolean;
}

export default function PrincipalSelector({
  type,
  label,
  selected,
  onAdd,
  onRemove,
  color,
  getDisplayValue,
  onRefresh,
  isRefreshing = false,
}: PrincipalSelectorProps) {
  // Fetch function based on type
  const fetchData = useCallback(async (search: string, page: number, perPage: number): Promise<PaginatedResponse<PrincipalEntity>> => {
    let response;
    switch (type) {
      case 'users':
        response = await apiClient.searchAdminUsers(search, page, perPage);
        break;
      case 'userGroups':
        response = await apiClient.searchAdminUserGroups(search, page, perPage);
        break;
      case 'roles':
        response = await apiClient.searchAdminRoles(search, page, perPage);
        break;
    }
    if (response.error) throw new Error(response.error);
    return response.data as PaginatedResponse<PrincipalEntity>;
  }, [type]);

  const handleChange = (value: string, selectedOption?: PrincipalEntity) => {
    if (value && selectedOption) {
      // For users, use email; for groups/roles, use name
      let displayValue: string;
      if (type === 'users') {
        const user = selectedOption as UserSummary;
        displayValue = user.email || user.id.toString();
      } else {
        const entity = selectedOption as UserGroupSummary | RoleSummary;
        displayValue = entity.name || entity.id.toString();
      }
      onAdd(displayValue);
    }
  };

  const getEntityDisplayName = (entity: PrincipalEntity): string => {
    if (type === 'users') {
      const user = entity as UserSummary;
      return user.email || user.name || `User ${user.id}`;
    }
    const groupOrRole = entity as UserGroupSummary | RoleSummary;
    return groupOrRole.name || `${label} ${groupOrRole.id}`;
  };

  return (
    <div className="mb-3">
      <Form.Label htmlFor={`${type}-selector`}>{label}</Form.Label>
      <Row>
        <Col md={8}>
          <ServerSearchableDropdown<PrincipalEntity>
            id={`${type}-selector`}
            fetchData={fetchData}
            value=""
            onChange={handleChange}
            getOptionValue={(entity) => entity.id.toString()}
            getOptionLabel={getEntityDisplayName}
            placeholder={`Select ${label.toLowerCase()}...`}
            onRefresh={onRefresh}
            isRefreshing={isRefreshing}
            size="sm"
            aria-label={label}
          />
        </Col>
      </Row>

      <div className="mt-2">
        {selected.map((item, idx) => (
          <Badge
            key={`${type}-${idx}-${item}`}
            bg={color}
            className="me-2 mb-2"
            style={{ fontSize: '0.9em' }}
          >
            {getDisplayValue(item)}
            <Button
              size="sm"
              variant="link"
              className="text-white p-0 ms-2"
              style={{ fontSize: '1.2em', textDecoration: 'none' }}
              aria-label={`Remove ${label.toLowerCase()} ${item}`}
              onClick={() => onRemove(item)}
            >
              ×
            </Button>
          </Badge>
        ))}
      </div>
    </div>
  );
}
